<template>
  <div class="select-products-dialog mt-5">
    <div>
      <mf-search
        placeholder="Busque produtos ou grupos"
        dense
        :disabled="chosenLoader"
        :value="searchValue"
        class="mb-5"
        autofocus
        hide-details
        :loading="loading"
        @input="querySelections"
      />

      <div class="d-flex justify-space-between align-center mt-5">
        <h2 class="container-subtitle">Resultados ({{ mergedSearchedItemsAndGroups.length }})</h2>
        <span v-if="searchedItems.length" :class="[isSelectAllEnabled ? 'link' : 'disabled-link']" @click="selectAllSearchedItems(searchedItems, chosenItems)">
          Selecionar todos produtos
        </span>
      </div>

      <div class="products-box left-side">
        <div v-if="loading" class="products-box-empty-state">
          <v-progress-circular size="20" indeterminate color="primary" class="mt-4" />
        </div>

        <div v-else-if="!mergedSearchedItemsAndGroups.length && !loading" class="products-box-empty-state">
          <mf-icon icon="search" size="24" color="cinza-l20" />
          <p>Busque um produto para exibir os resultados</p>
        </div>

        <DynamicScroller
          v-else
          v-slot="{ item, index, active }"
          class="vue-recycle-scroller scroller ready direction-vertical"
          :items="mergedSearchedItemsAndGroups"
          :min-item-size="20"
          key-field="_id"
        >
          <DynamicScrollerItem :item="item" :active="active" :data-index="index">
            <mf-tooltip top variant="secondary" max-width="300px">
              <template #activator>
                <div
                  class="result-list-item d-flex align-center justify-space-between"
                  @click="() => (isGroup(item) ? checkGroupProducts(item, productsGroups, chosenItems) : setItem(item, searchedItems, chosenItems))"
                >
                  <div class="d-flex align-center product-name-container">
                    <mf-icon v-if="isGroup(item)" fill color="marca-1" icon="folder" />
                    <span>{{ isGroup(item) ? `${item.name}` : `${item.sku} - ${item.name}` }}</span>
                  </div>
                  <mf-icon fill color="cinza-0" icon="chevron_right" size="20" />
                </div>
              </template>
              {{ item.name }}
            </mf-tooltip>
          </DynamicScrollerItem>
        </DynamicScroller>
      </div>
    </div>

    <div>
      <div class="filter-button ml-4">
        <filters-menu activator-type="button" :filters="['measurementUnits']" />
      </div>

      <h2 class="container-subtitle mt-5 ml-4">Selecionados ({{ chosenItems.length }})</h2>

      <div class="products-box right-side">
        <div v-if="chosenLoader" class="d-flex align-center justify-center">
          <v-progress-circular size="20" indeterminate color="primary" class="mt-4" />
        </div>

        <div v-else-if="!chosenItems.length && !chosenLoader" class="products-box-empty-state">
          <mf-icon fill icon="folder_open" size="24" color="cinza-l20" />
          <p>Nenhum produto selecionado</p>
        </div>

        <div class="right-side-items-list">
          <p v-if="!chosenLoader && chosenItems.length" class="clear-all mb-0" @click="clearAll">Limpar todos</p>
          <DynamicScroller
            v-if="chosenItems.length"
            v-slot="{ item, index, active }"
            class="vue-recycle-scroller selected-items-scroller ready direction-vertical"
            :items="formattedChosenItems.slice().reverse()"
            :min-item-size="20"
            key-field="_id"
          >
            <DynamicScrollerItem :item="item" :active="active" :data-index="index">
              <div v-if="!item.isGroup">
                <mf-tooltip top variant="secondary" max-width="300px">
                  <template #activator>
                    <div class="d-flex align-center normal-product">
                      <p>{{ item.sku }} - {{ item.name }}</p>
                      <mf-icon v-if="!item.disabled" fill color="cinza-d0" icon="close" size="20" @click="setItem(item, chosenItems, searchedItems, true)" />
                    </div>
                  </template>
                  {{ item.name }}
                </mf-tooltip>
              </div>

              <div v-else class="group-container my-1">
                <mf-tooltip top variant="secondary" max-width="300px">
                  <template #activator>
                    <div class="d-flex align-center normal-product">
                      <p>{{ item.name }}</p>
                      <mf-icon v-if="!item.disabled" fill color="cinza-d0" icon="close" size="20" @click="removeGroup(item, chosenItems, searchedItems)" />
                    </div>
                  </template>
                  {{ item.name }}
                </mf-tooltip>

                <mf-tooltip
                  v-for="(product, chosenProductIndex) in item.products"
                  :key="`${product.name}-${chosenProductIndex}`"
                  top
                  variant="secondary"
                  max-width="300px"
                >
                  <template #activator>
                    <div class="d-flex align-center normal-product px-5">
                      <p>{{ product.sku }} - {{ product.name }}</p>
                      <mf-icon v-if="!item.disabled" fill color="cinza-d0" icon="close" size="20" @click="setItem(product, chosenItems, searchedItems, true)" />
                    </div>
                  </template>
                  {{ product.name }}
                </mf-tooltip>
              </div>
            </DynamicScrollerItem>
          </DynamicScroller>
        </div>
      </div>
    </div>
    <mf-toggle v-model="sortByRelevance" label="Ordenar produtos por relevância de venda" class="pl-1 mt-4" hide-details />
  </div>
</template>

<script>
import { QUERY_GET_GROUP_PRODUCTS } from '@/modules/produtos/gerenciamento-produtos/graphql'
import { QUERY_GET_PRODUCTS_AND_GROUPS, PRODUCTS_SUBSCRIPTION, QUERY_ACTIVATE_IMPORTED_PRODUCTS } from '@/modules/produtos/graphql'
import { findIndex, find, capitalize } from 'lodash'
import CSV from 'csv-string'
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import { v4 as uuidv4 } from 'uuid'
import { wsClient } from '@/services/webSocket'
import { mapState } from 'vuex'
export default {
  components: {
    DynamicScroller,
    DynamicScrollerItem,
    FiltersMenu: () => import('@/components/FiltersMenu.vue'),
  },
  props: {
    campaign: {
      type: Object,
      default: () => {},
    },
    offerType: {
      type: String,
      default: '',
    },
    showActions: {
      type: Boolean,
      default: true,
    },
    filterDuplicateProducts: {
      type: Boolean,
      default: true,
    },
    hideCsvUpload: {
      type: Boolean,
      default: false,
    },
    disableGroups: {
      type: Boolean,
      default: false,
    },
    defaultImport: {
      type: Boolean,
      default: true,
    },
    isLengthLimited: {
      type: Boolean,
      default: false,
    },
    maxSelectedItems: {
      type: Number,
      default: 0,
    },
    window: {
      type: Number,
      default: 0,
    },
    titleDialog: {
      type: String,
      default: '',
    },
    selectedProducts: {
      type: Array,
      default: () => [],
    },
    onlyActive: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    roomId: '',
    searchValue: '',
    sortByRelevance: true,
    loading: false,
    chosenLoader: false,
    searchedItems: [],
    chosenItems: [],
    chosenGroupsItems: [],
    productsGroups: [],
    capitalize: capitalize,
    fileContent: null,
    fileTypes: [
      'text/comma-separated-values',
      'text/csv',
      'application/csv',
      'application/excel',
      'application/vnd.ms-excel',
      'application/vnd.msexcel',
      'text/anytext',
      '',
    ],
  }),
  computed: {
    ...mapState({
      stateFilters: (state) => state.filters,
    }),
    // return an array of items based on the chosen products, grouped by group
    formattedChosenItems() {
      return this.chosenItems.reduce((accumulator, current) => {
        if (current.isGroup) {
          const productIndex = accumulator.findIndex((item) => item._id && item._id === current.groupInfo._id)
          if (productIndex >= 0) {
            accumulator[productIndex].products.push(current)
          } else {
            const {
              nameGroup,
              isGroup,
              disabled,
              groupInfo: { _id, __typename, image_url, rules_exhibition, categories_final_consumer },
              ...product
            } = current
            const group = {
              name: nameGroup,
              disabled: disabled || false,
              isGroup,
              _id,
              __typename,
              products: [product],
              image_url,
              rules_exhibition,
              categories_final_consumer,
            }
            accumulator.push(group)
          }
        } else {
          accumulator.push({
            ...current,
            _id: current._id || current.sku,
          })
        }
        return accumulator
      }, [])
    },
    isOffersByUnit() {
      return this.$getAllowedModules()['offers_by_unit'] && this.offerType === 'dynamic_pricing_percentage_offer_sales'
    },
    isSelectAllEnabled() {
      if (this.isLengthLimited && this.formattedChosenItems.length >= this.maxSelectedItems) {
        return false
      }
      if (!this.searchedItems.length) {
        return false
      }
      return true
    },
    mergedSearchedItemsAndGroups() {
      return [...this.productsGroups, ...this.searchedItems]
    },
  },
  watch: {
    sortByRelevance() {
      this.searchItem(this.searchValue)
    },
    window() {
      if (this.window && this.window === 1) {
        this.setSelectedItems()
      }
    },
    chosenLoader(newValue) {
      this.$emit('change-import-loader', newValue)
    },
    'stateFilters.measureUnit'() {
      this.querySelections(this.searchValue)
    },
  },
  async mounted() {
    const roomId = uuidv4()
    this.roomId = roomId
    this.setSelectedItems()

    const self = this
    await wsClient.subscribe(
      {
        query: PRODUCTS_SUBSCRIPTION,
        operationName: 'ValidImportedProducts',
        variables: {
          roomId: this.roomId,
        },
      },
      {
        next({ data: { validImportedProducts } }) {
          const { availableTotal, data, deliveredTotal } = validImportedProducts

          if (data?.length > 0) {
            data.forEach((item) => {
              const indexOnSearched = findIndex(self.searchedItems, item)
              const indexOnChosen = findIndex(self.chosenItems, item)
              if (indexOnChosen > -1) return
              self.searchedItems.splice(indexOnSearched, 1)
            })
            const filterProducts = self.removeChosenItems(data)

            self.chosenItems.push(...filterProducts)
            self.chosenGroupsItems.push(...filterProducts)
          }
          if (availableTotal === deliveredTotal) self.chosenLoader = false
        },
        error(error) {
          console.error(error)
          self.chosenLoader = false
          self.$snackbar({ message: 'Erro ao carregar produtos, tente novamente mais tarde.', snackbarColor: 'error' })
        },
      }
    )
  },
  methods: {
    isGroup(item) {
      return item.__typename === 'ProductsGroup'
    },
    uploadCsv() {
      this.$refs.inputFile.activateInput()
    },
    selectAllSearchedItems() {
      if (!this.isSelectAllEnabled) {
        return
      }
      if (this.isLengthLimited && this.formattedChosenItems.length >= this.maxSelectedItems) {
        this.$snackbar({ message: `Máximo de ${this.maxSelectedItems} produto atingido`, snackbarColor: 'error' })
        return
      }
      let numberOfItemsToInsert = this.searchedItems.length
      if (this.isLengthLimited && numberOfItemsToInsert + this.formattedChosenItems.length >= this.maxSelectedItems) {
        numberOfItemsToInsert = Math.min(numberOfItemsToInsert, this.maxSelectedItems - this.formattedChosenItems.length)
        this.$snackbar({ message: `Máximo de ${this.maxSelectedItems} produto atingido` })
      }
      const itemsToInsert = this.searchedItems.splice(0, numberOfItemsToInsert)
      // TODO: Groups - if (!itemObject.isGroup) pushList.push(itemObject)

      this.chosenItems.push(...itemsToInsert)
      this.updateGroupList()
    },
    updatedFileInput() {
      const fileType = this.$refs.inputFile.fileType
      if (!this.fileContent) return
      if (!this.fileTypes.includes(fileType)) {
        this.$snackbar({ message: `Permitido apenas arquivos em .csv, o arquivo tem o formato: ${fileType}`, snackbarColor: 'error' })
        return
      }
      const idList = CSV.parse(this.fileContent)
        .map((line) => line[0])
        .filter((element) => /^\d+$/.test(element.trim()))
      if (!idList.length) {
        this.$snackbar({ message: `Não foi possível importar os dados do arquivo`, snackbarColor: 'error' })
        return
      }
      this.getSelectedProducts(idList)
    },
    setSelectedItems() {
      this.chosenItems = []
      this.chosenGroupsItems = []
      if (this.selectedProducts.length) {
        this.selectedProducts.forEach((product) => {
          if (product.products) {
            this.setGroup(product, [product], this.chosenItems)
            return
          } else if (product.isGroup) {
            this.chosenItems.push(product)
          }
          this.setItem(product, [product], this.chosenItems)
        })
      }
    },
    querySelections(query) {
      this.searchValue = query
      if (!query || query.length < 2) return
      this.loading = true
      const self = this
      window.clearTimeout(this.timeout)
      this.timeout = window.setTimeout(() => {
        self.searchItem(query)
      }, 500)
    },
    searchItem(query) {
      const variables = { search: query }
      if (this.isOffersByUnit) {
        variables.units = this.campaign.units
      }
      if (this.onlyActive) {
        variables.active = true
      }
      if (this.sortByRelevance) {
        variables.sort = ['eanFullMatch', 'skuFullMatch', 'active', 'relevance_score', 'name']
        variables.sort_direction = ['DESC', 'DESC', 'DESC', 'DESC', 'ASC']
      }
      if (this.stateFilters.measureUnit) {
        variables.measure_acronym = this.stateFilters.measureUnit
      }
      this.$apollo
        .query({
          query: QUERY_GET_PRODUCTS_AND_GROUPS,
          variables,
          fetchPolicy: 'no-cache',
        })
        .then(({ data: { products: { products } = {}, productsGroups: { productsGroups } } = {} }) => {
          if (!this.disableGroups) {
            this.productsGroups = this.removeChosenGroups(productsGroups)
          }
          this.searchedItems = this.removeChosenItems(products)
          this.loading = false
        })
    },
    clearAll() {
      this.loading = true
      this.chosenItems = this.chosenItems.filter((group) => group.disabled)
      this.chosenGroupsItems = this.chosenGroupsItems.filter((group) => group.disabled)

      if (this.searchValue) this.searchItem(this.searchValue)
      else this.loading = false
    },
    setItem(item, removeList, pushList, isRemoving = false) {
      if (this.isLengthLimited && this.formattedChosenItems.length >= this.maxSelectedItems && !isRemoving) {
        this.$snackbar({ message: `Máximo de ${this.maxSelectedItems} produto atingido`, snackbarColor: 'error' })
        return
      }

      const { sku } = item
      const itemObject = find(removeList, {
        sku,
      })
      if (!itemObject) return
      const itemObjectIndex = findIndex(removeList, itemObject)
      removeList.splice(itemObjectIndex, 1)

      if (!itemObject.isGroup) pushList.push(itemObject)

      this.updateGroupList()
    },
    async checkGroupProducts(group, removeList, pushList) {
      if (this.isLengthLimited && this.formattedChosenItems.length >= this.maxSelectedItems && !this.chosenLoader) {
        this.$snackbar({ message: `Máximo de ${this.maxSelectedItems} produto atingido`, snackbarColor: 'error' })
        return
      }

      let newGroup = group
      if (!group.products) {
        this.chosenLoader = true
        const {
          data: { productsGroup },
        } = await this.getGroupProducts(group._id)
        newGroup = productsGroup
        this.chosenLoader = false
      }

      this.setGroup(newGroup, removeList, pushList)
    },
    async getGroupProducts(id) {
      try {
        const variables = { _id: id }
        if (this.onlyActive) {
          variables.active = true
        }
        return await this.$apollo.query({
          query: QUERY_GET_GROUP_PRODUCTS,
          variables: {
            _id: id,
          },
        })
      } catch (error) {
        this.$snackbar({ message: 'Erro ao buscar produtos.', snackbarColor: 'error' })
      }
    },
    setGroup(group, removeList, pushList) {
      const itemGroup = find(removeList, {
        _id: group._id,
      })

      if (!itemGroup) return
      const itemGroupIndex = findIndex(removeList, itemGroup)
      const { name, ...groupInfo } = removeList[itemGroupIndex]
      const productsFormatted = group.products.map((product) => {
        return {
          isGroup: true,
          nameGroup: name,
          groupInfo,
          product: {
            image: product.image,
            ean: product.ean,
            eans: product.eans.length > 1 ? product.eans : [product.ean],
          },
          ...product,
        }
      })
      removeList.splice(itemGroupIndex, 1)
      pushList.push(...productsFormatted)

      this.updateGroupList()
    },
    updateGroupList() {
      this.chosenGroupsItems = this.formattedChosenItems
    },
    removeGroup(group, removeList, pushList) {
      this.productsGroups.push(group)
      group.products.forEach((product) => {
        this.setItem(product, removeList, pushList, true)
      })
    },
    removeChosenItems(responseData) {
      const array = responseData
      for (let item of this.chosenItems) {
        let itemIndex = array.findIndex((product) => product.sku === item.sku)
        if (itemIndex > -1) {
          array.splice(itemIndex, 1)
        }
      }

      return array
    },
    removeChosenGroups(responseData) {
      const array = responseData
      return array.reduce((accumulator, current) => {
        const wasChose = this.chosenItems.map((product) => product.nameGroup).includes(current.name)

        if (!wasChose) {
          accumulator.push(current)
        }

        return accumulator
      }, [])
    },
    cancel() {
      this.$emit('cancel')
      this.clearData()
    },
    confirmChanges(productsArray, productsAndGroupsArray) {
      const items = {
        products: productsArray,
        productsAndGroups: productsAndGroupsArray,
      }
      this.$emit('confirm-changes', items)
      this.clearData()
    },
    clearData() {
      this.searchValue = ''
      this.searchedItems = []
      this.chosenItems = []
      this.chosenGroupsItems = []
      this.productsGroups = []
    },
    checkValuesAndConfirmChanges() {
      let productsArray = []

      if (this.filterDuplicateProducts) {
        productsArray = this.chosenItems.reduce((accumulator, currentProduct) => {
          const position = accumulator.findIndex((item) => item.sku && item.sku === currentProduct.sku)

          if (position === -1) {
            accumulator.push(currentProduct)
          }

          return accumulator
        }, [])
      } else {
        productsArray = this.chosenItems
      }
      this.confirmChanges(productsArray, this.chosenGroupsItems)
    },
    async getSelectedProducts({ eans = [], skus = [] }) {
      this.chosenLoader = true
      try {
        const variables = {
          eans,
          skus,
          roomId: this.roomId,
        }
        if (this.isOffersByUnit) {
          variables.units = this.campaign.units
        }

        await this.$apollo.query({
          query: QUERY_ACTIVATE_IMPORTED_PRODUCTS,
          variables,
          fetchPolicy: 'no-cache',
          context: {
            uri: this.$microservicesUrls['asyncJobs'],
          },
        })
      } catch (error) {
        console.log(error)
        this.$snackbar({ message: 'Erro ao carregar produtos, tente novamente mais tarde.', snackbarColor: 'error' })
        this.chosenLoader = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.select-products-dialog {
  display: grid;
  grid-template-columns: 1fr 1fr;

  .container-subtitle {
    font-size: 16px;
    font-weight: 400;
    color: $cinza-d10;
    line-height: 19.2px;
  }

  .filter-button {
    padding-bottom: 2px;
  }

  .left-side {
    background-color: $cinza-l90;
  }

  .right-side {
    background-color: $cinza-l80;
  }

  .products-box {
    margin-top: 16px;
    width: 100%;
    height: 264px;
    .products-box-empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 4px;
      width: 100%;
      height: 100%;

      p {
        margin: 0;
        font-size: 14px;
        color: $cinza-l20;
      }
    }

    .scroller {
      height: 264px !important;
      overflow-y: auto !important;
    }

    .selected-items-scroller {
      height: 214px !important;
      overflow-y: auto !important;
    }

    .result-list-item {
      padding: 12px 12px 12px 24px;
      gap: 12px;
      cursor: pointer;
      border-bottom: 1px solid $cinza-l70;
      &:hover {
        background-color: $cinza-l80;
      }

      span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 430px;
      }
      .product-name-container {
        gap: 12px;
      }
    }
  }

  .right-side-items-list {
    .clear-all {
      height: 50px;
      padding: 16px;
      font-size: 14px;
      color: $cinza-0;
      text-decoration: underline;
      cursor: pointer;
    }

    .normal-product {
      width: 100%;
      gap: 12px;
      padding: 8px 12px;
      p {
        margin: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 460px;
        color: $cinza-d10;
        font-size: 14px;
      }

      span {
        cursor: pointer;
      }
    }

    .group-container {
      background-color: $cinza-l70;
    }
  }
}
</style>
